import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import UiIcon from '../components/UiIcon';
import AmvTablePager from '../components/admin/AmvTablePager';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';

const ENDPOINTS = {
  list: '/api/asociacion-recetas/administrativos/visibilidad',
  create: '/api/asociacion-recetas/administrativos',
  patch: (id) => `/api/asociacion-recetas/administrativos/${id}/visibilidad`,
  update: (id) => `/api/asociacion-recetas/administrativos/${id}`,
  delete: (id) => `/api/asociacion-recetas/administrativos/${id}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function showAmvToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function sortAdministrativosByName(list) {
  return [...list].sort((a, b) => {
    const aName = String(a?.nombre || '').trim();
    const bName = String(b?.nombre || '').trim();
    return aName.localeCompare(bName, 'es', { sensitivity: 'base' });
  });
}

function AdminAdministrativosVisibilidad() {
  const [administrativos, setAdministrativos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [savingIds, setSavingIds] = useState({});
  const [deletingIds, setDeletingIds] = useState({});
  const [newNombre, setNewNombre] = useState('');
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const loadAdministrativos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.administrativos) ? json.administrativos : [];
      setAdministrativos(sortAdministrativosByName(list));
    } catch (e) {
      setError(e.message || 'No se pudo cargar el listado de administrativos.');
      setAdministrativos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAdministrativos();
  }, [loadAdministrativos]);

  const sortedAdministrativos = useMemo(
    () => sortAdministrativosByName(administrativos),
    [administrativos]
  );

  const filteredAdministrativos = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return sortedAdministrativos;
    return sortedAdministrativos.filter((a) => String(a.nombre || '').toLowerCase().includes(q));
  }, [sortedAdministrativos, search]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredAdministrativos.length / pageSize));
  }, [filteredAdministrativos.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredAdministrativos;
    const start = (page - 1) * pageSize;
    return filteredAdministrativos.slice(start, start + pageSize);
  }, [filteredAdministrativos, page, pageSize]);

  const rangeStart = filteredAdministrativos.length === 0
    ? 0
    : pageSize <= 0
      ? 1
      : (page - 1) * pageSize + 1;
  const rangeEnd = pageSize <= 0
    ? filteredAdministrativos.length
    : Math.min(page * pageSize, filteredAdministrativos.length);

  const visibleCount = useMemo(
    () => administrativos.filter((a) => Number(a.is_visible) === 1).length,
    [administrativos]
  );
  const hiddenCount = Math.max(0, administrativos.length - visibleCount);
  const searchActive = Boolean(String(search || '').trim());

  async function handleCreate(e) {
    e.preventDefault();
    const nombre = String(newNombre || '').trim();
    if (!nombre) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const created = json.administrativo;
      if (created) {
        setAdministrativos((prev) => sortAdministrativosByName([...prev, created]));
      } else {
        await loadAdministrativos();
      }
      setNewNombre('');
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo crear',
        text: err.message || 'Error al agregar administrativo.'
      });
    } finally {
      setCreating(false);
    }
  }

  async function requestToggleVisibilidad(row, currentlyVisible) {
    const nextVisible = !currentlyVisible;
    const nombre = String(row.nombre || '').trim();
    const confirm = await fireConfirmDialog({
      title: nextVisible ? 'Mostrar en emisión' : 'Ocultar de emisión',
      html: buildCrmSwalBody({
        lead: nextVisible
          ? 'La persona aparecerá al vincular usuarios administrativos y al emitir tratamientos.'
          : 'Dejará de aparecer en los selectores operativos.',
        details: [{ label: 'Administrativo', value: nombre }],
        items: nextVisible
          ? ['Podrá vincularse a usuarios CRM con rol administrativo', 'Podrá elegirse al aprobar con emisor Administrativo']
          : ['No se elimina del catálogo', 'Podés reactivarlo cuando quieras']
      }),
      icon: nextVisible ? 'question' : 'warning',
      confirmButtonText: nextVisible ? 'Mostrar' : 'Ocultar',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;
    await toggleVisibilidad(row, nextVisible);
  }

  function openEdit(row) {
    setEditRow(row);
    setEditNombre(String(row.nombre || ''));
  }

  function closeEdit() {
    if (editSaving) return;
    setEditRow(null);
    setEditNombre('');
  }

  useEffect(() => {
    if (!editRow) return undefined;
    const onKeyDown = (evt) => {
      if (evt.key === 'Escape' && !editSaving) {
        setEditRow(null);
        setEditNombre('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editRow, editSaving]);

  async function submitEdit(e) {
    e.preventDefault();
    if (!editRow || editSaving) return;

    const nombreActual = String(editRow.nombre || '').trim();
    const nombre = String(editNombre || '').trim();
    if (!nombre) return;
    if (nombre === nombreActual) {
      closeEdit();
      return;
    }

    const id = Number(editRow.id);
    if (!Number.isFinite(id) || id <= 0) return;

    setEditSaving(true);
    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json.administrativo || { ...editRow, nombre };
      setAdministrativos((prev) => sortAdministrativosByName(
        prev.map((a) => (Number(a.id) === id ? { ...a, ...updated } : a))
      ));
      closeEdit();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo editar',
        text: err.message || 'Error al actualizar el administrativo.'
      });
    } finally {
      setEditSaving(false);
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  async function requestDelete(row) {
    const nombre = String(row.nombre || '').trim();
    const confirm = await fireConfirmDialog({
      title: 'Eliminar del catálogo',
      html: buildCrmSwalBody({
        lead: 'Se quitará esta persona del catálogo de administrativos.',
        details: [{ label: 'Administrativo', value: nombre }],
        items: [
          'No afecta pedidos ni usuarios ya vinculados (en ese caso no se puede eliminar)',
          'Esta acción no se puede deshacer'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) return;

    const deletedNombre = String(row.nombre || '').trim();

    setDeletingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      setAdministrativos((prev) => prev.filter((a) => Number(a.id) !== id));
      showAmvToast({
        title: 'Eliminado del catálogo',
        text: deletedNombre
          ? `${deletedNombre} fue quitado del listado de administrativos.`
          : 'La persona fue quitada del catálogo.'
      });
    } catch (e) {
      const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const raw = String(e?.message || '').trim();
      const isBlocked = /^no se puede eliminar/i.test(raw);
      const reason = isBlocked ? raw.replace(/^no se puede eliminar:?\s*/i, '').trim() : '';
      const quien = String(row.nombre || '').trim() || 'Esta persona';
      fireCrmAlert({
        icon: isBlocked ? 'warning' : 'error',
        title: isBlocked ? 'No se puede eliminar' : 'No se pudo eliminar',
        html: buildCrmSwalBody({
          lead: isBlocked
            ? `<strong>${esc(quien)}</strong> no se puede quitar del catálogo porque ${esc(reason) || 'tiene registros asociados'}.`
            : esc(raw || 'Ocurrió un error al eliminar el administrativo.'),
          footnote: isBlocked
            ? 'Se mantiene en el catálogo para conservar la trazabilidad de su historial.'
            : ''
        }),
        confirmButtonText: 'Entendido',
        width: 'min(420px, 92vw)'
      });
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  async function toggleVisibilidad(row, nextVisible) {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) return;
    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.patch(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_visible: nextVisible ? 1 : 0 })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json.administrativo || { ...row, is_visible: nextVisible ? 1 : 0 };
      setAdministrativos((prev) => sortAdministrativosByName(
        prev.map((a) => (Number(a.id) === id ? { ...a, ...updated } : a))
      ));
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar',
        text: e.message || 'Error al cambiar la visibilidad.'
      });
    } finally {
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true" style={{ height: '100vh', margin: '15px' }}>
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando administrativos</div>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Consultando catálogo…</p>
      </div>
    );
  }

  return (
    <section className="crm-amv-page crm-gd-page crm-pbody">
      {error ? (
        <div className="crm-amv-alert" role="alert">{error}</div>
      ) : null}

      <section className="crm-amv-hero-panel" aria-labelledby="aa-create-title">
        <div className="crm-amv-hero-panel__head">
          <div className="crm-amv-hero-panel__brand">
            <div className="crm-amv-hero-panel__icon" aria-hidden="true">🧑‍💼</div>
            <div className="crm-amv-hero-panel__titles">
              <p className="crm-amv-hero-panel__eyebrow">Alta en catálogo</p>
              <h2 id="aa-create-title" className="crm-amv-hero-panel__title">
                Agregar persona administrativa
              </h2>
            </div>
          </div>
          <div className="crm-amv-hero-panel__stats" aria-label="Resumen del catálogo">
            <form className="crm-amv-hero-panel__form" onSubmit={handleCreate} style={{ flexWrap: 'nowrap' }}>
              <label className="crm-amv-hero-panel__field" htmlFor="aa-new-nombre">
                <input
                  id="aa-new-nombre"
                  className="crm-amv-hero-panel__input"
                  type="text"
                  placeholder="Ej: María González"
                  value={newNombre}
                  onChange={(e) => setNewNombre(e.target.value)}
                  disabled={creating}
                  autoComplete="off"
                />
              </label>
              <button
                type="submit"
                className="crm-amv-hero-panel__submit"
                disabled={creating || !String(newNombre || '').trim()}
              >
                {creating ? 'Guardando…' : 'Agregar'}
              </button>
            </form>
            <div className="crm-amv-hero-stat">
              <span className="crm-amv-hero-stat__num">{administrativos.length}</span>
              <span className="crm-amv-hero-stat__label">Total</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--visible">
              <span className="crm-amv-hero-stat__num">{visibleCount}</span>
              <span className="crm-amv-hero-stat__label">Activos</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--hidden">
              <span className="crm-amv-hero-stat__num">{hiddenCount}</span>
              <span className="crm-amv-hero-stat__label">Inactivos</span>
            </div>
          </div>
        </div>
      </section>

      {editRow ? (
        <div
          className="crm-amv-edit-backdrop"
          role="presentation"
          onClick={closeEdit}
        >
          <div
            className="crm-amv-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aa-edit-title"
            onClick={(evt) => evt.stopPropagation()}
          >
            <div className="crm-amv-edit-modal__head">
              <div className="crm-amv-edit-modal__brand">
                <span className="crm-amv-edit-modal__glyph" aria-hidden="true">
                  <UiIcon name="edit" size={16} />
                </span>
                <div>
                  <p className="crm-amv-edit-modal__eyebrow">Catálogo</p>
                  <h3 id="aa-edit-title" className="crm-amv-edit-modal__title">Editar nombre</h3>
                </div>
              </div>
              <button
                type="button"
                className="crm-amv-edit-modal__close"
                onClick={closeEdit}
                disabled={editSaving}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <form className="crm-amv-edit-modal__form" onSubmit={submitEdit}>
              <label className="crm-amv-edit-modal__field" htmlFor="aa-edit-nombre">
                <span className="crm-amv-edit-modal__field-label">Nombre y apellido</span>
                <input
                  id="aa-edit-nombre"
                  className="crm-amv-edit-modal__input"
                  type="text"
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  disabled={editSaving}
                  autoComplete="off"
                  autoFocus
                  placeholder="Nombre completo"
                />
              </label>
              <p className="crm-amv-edit-modal__hint">
                Se verá al vincular usuarios CRM y al emitir tratamientos.
              </p>
              <div className="crm-amv-edit-modal__actions">
                <button
                  type="button"
                  className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost"
                  onClick={closeEdit}
                  disabled={editSaving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--primary"
                  disabled={editSaving || !String(editNombre || '').trim()}
                >
                  {editSaving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="crm-amv-card crm-gd-card">
        <div className="crm-amv-datatable-toolbar">
          <div className="crm-amv-datatable-toolbar__search">
            <CwListSearch
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nombre…"
            />
          </div>
          <div className="crm-amv-datatable-toolbar__field">
            <label htmlFor="aa-page-size">Mostrar</label>
            <select
              id="aa-page-size"
              className="crm-amv-select"
              value={pageSize <= 0 ? 'ALL' : String(pageSize)}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === 'ALL' ? 0 : Number(v));
                setPage(1);
              }}
              aria-label="Registros por página"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              <option value="ALL">Todos</option>
            </select>
            <span className="crm-amv-datatable-toolbar__suffix">por página</span>
          </div>
        </div>

        {administrativos.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--empty" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">👤</span>
            <p className="crm-amv-state__title">Sin administrativos en catálogo</p>
            <p className="crm-amv-state__hint">Agregá personas con el formulario de arriba. Luego activá su visibilidad.</p>
          </div>
        ) : filteredAdministrativos.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--no-match" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">🔎</span>
            <p className="crm-amv-state__title">Sin coincidencias</p>
            {searchActive ? (
              <p className="crm-amv-state__term" title={search.trim()}>
                {search.trim().length > 48 ? `${search.trim().slice(0, 48)}…` : search.trim()}
              </p>
            ) : null}
            <p className="crm-amv-state__hint">
              No hay administrativos que coincidan con la búsqueda. Probá con otro texto o borrá el filtro.
            </p>
            <button
              type="button"
              className="crm-amv-state__btn"
              onClick={() => { setSearch(''); setPage(1); }}
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <>
            <div className="crm-amv-table-wrap">
              <table className="crm-amv-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th className="crm-amv-table__th-switch">Visibilidad</th>
                    <th className="crm-amv-table__th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((a) => {
                    const isVisible = Number(a.is_visible) === 1;
                    const rowId = Number(a.id);
                    const isSaving = Boolean(savingIds[rowId]);
                    const isDeleting = Boolean(deletingIds[rowId]);
                    const isBusy = isSaving || isDeleting;
                    return (
                      <tr
                        key={a.id}
                        className={`crm-amv-table__row${isVisible ? ' is-visible' : ''}${isBusy ? ' is-saving' : ''}`}
                      >
                        <td>
                          <div className="crm-amv-table__doctor">
                            {isVisible ? (
                              <span className="crm-amv-table__doctor-dot" aria-hidden="true" />
                            ) : null}
                            <span className="crm-amv-table__doctor-name">{a.nombre}</span>
                          </div>
                        </td>
                        <td>
                          {isVisible ? (
                            <span className="crm-amv-pill crm-amv-pill--visible">Visible</span>
                          ) : (
                            <span className="crm-amv-pill crm-amv-pill--hidden">Oculto</span>
                          )}
                        </td>
                        <td className="crm-amv-table__switch-cell">
                          <label
                            className="crm-amv-row__switch-wrap"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isBusy) return;
                              void requestToggleVisibilidad(a, isVisible);
                            }}
                          >
                            <span className="crm-amv-row__switch-label">
                              {isVisible ? 'Activo' : 'Inactivo'}
                            </span>
                            <span className="crm-amv-switch-control">
                              <input
                                className="crm-amv-switch-input"
                                type="checkbox"
                                checked={isVisible}
                                readOnly
                                tabIndex={-1}
                                aria-hidden="true"
                              />
                              <span
                                className="crm-amv-switch-track"
                                role="switch"
                                aria-checked={isVisible}
                                aria-label={`${isVisible ? 'Ocultar' : 'Mostrar'} a ${a.nombre}`}
                              />
                            </span>
                          </label>
                        </td>
                        <td className="crm-amv-table__actions-cell">
                          <div className="crm-amv-row-actions">
                            <button
                              type="button"
                              className="crm-amv-row-action-btn"
                              onClick={() => {
                                if (isBusy) return;
                                openEdit(a);
                              }}
                              disabled={isBusy}
                              title={`Editar ${a.nombre}`}
                              aria-label={`Editar ${a.nombre}`}
                            >
                              <UiIcon name="edit" size={14} />
                            </button>
                            <button
                              type="button"
                              className="crm-amv-row-action-btn crm-amv-row-action-btn--danger"
                              onClick={() => {
                                if (isBusy) return;
                                void requestDelete(a);
                              }}
                              disabled={isBusy}
                              title={`Eliminar ${a.nombre}`}
                              aria-label={`Eliminar ${a.nombre}`}
                            >
                              <UiIcon name="trash" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <AmvTablePager
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              filteredCount={filteredAdministrativos.length}
              totalCount={administrativos.length}
              searchActive={searchActive}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              entityLabel="administrativos"
            />
          </>
        )}
      </div>
    </section>
  );
}

export default AdminAdministrativosVisibilidad;
